import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './Quiz.css';
import './quiz.mobile.css';

// Preserved component for future use
const PurposeSelection = ({ onSelect, selectedPurpose }) => {
  const purposeOptions = [
    {
      value: 'engagement',
      label: 'Engagement Ring',
      description: 'Selecting a timeless symbol of commitment',
      icon: '💍'
    },
    {
      value: 'anniversary',
      label: 'Anniversary or Special Gift',
      description: 'Marking a significant milestone with elegance',
      icon: '🎁'
    },
    {
      value: 'browsing',
      label: 'Just Browsing',
      description: 'Exploring our curated collection of fine jewelry',
      icon: '🔍'
    },
    {
      value: 'other',
      label: 'Other',
      description: 'Discovering unique pieces for special occasions',
      icon: '💫'
    }
  ];

  return (
    <div className="quiz-step">
      <div className="step-header">
        <span className="step-label">Purpose Selection</span>
        <h2>What are you shopping for today?</h2>
        <p className="step-description">This helps us tailor our recommendations to your specific needs.</p>
      </div>
      <div className="step-content">
        <div className="options-grid">
          {purposeOptions.map(option => (
            <button
              key={option.value}
              className={`option-button ${selectedPurpose === option.value ? 'selected' : ''}`}
              onClick={() => onSelect(option.value)}
              data-value={option.value}
            >
              <div className="option-icon">{option.icon}</div>
              <div className="option-content">
                <h3>{option.label}</h3>
                <p>{option.description}</p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const Quiz = () => {
  const navigate = useNavigate();
  const { step: urlStep } = useParams();
  const [step, setStep] = useState(parseInt(urlStep) || 1);
  const [answers, setAnswers] = useState({
    email: localStorage.getItem('userEmail') || '',
    phone: '',
    budget: '',
    cut: [],
    carat: '',
    clarity: '',
    color: '',
    shape: '',
    cutQuality: '',
    includeSetting: true
  });

  const [cutViewMode, setCutViewMode] = useState({});

  useEffect(() => {
    // console.log(answers);
    const currentStep = parseInt(urlStep) || 1;
    if (currentStep !== step) {
      setStep(currentStep);
    }
  }, [urlStep]);

  const formatPhoneNumber = (value) => {
    // Remove all non-numeric characters
    const numbers = value.replace(/\D/g, '');
    
    // Format the number as (XXX) XXX-XXXX
    if (numbers.length <= 3) {
      return numbers;
    } else if (numbers.length <= 6) {
      return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    } else {
      return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
    }
  };

  const handleInputChange = (field, value) => {
    if (field === 'phone') {
      const formattedNumber = formatPhoneNumber(value);
      setAnswers(prev => ({
        ...prev,
        phone: formattedNumber
      }));
    } else if (field === 'cut') {
      setAnswers(prev => ({
        ...prev,
        cut: prev.cut.includes(value)
          ? prev.cut.filter(c => c !== value)
          : [...prev.cut, value]
      }));
    } else {
      setAnswers(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const toggleCutView = (cutName) => {
    setCutViewMode(prev => ({
      ...prev,
      [cutName]: prev[cutName] === 'diagram' ? 'photo' : 'diagram'
    }));
  };

  const handleFormSubmit = (e) => {
    // Only prevent default if there's an event (direct form submission)
    if (e) {
      e.preventDefault();
    }
    
    const encode = (data) => {
      return Object.keys(data)
        .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
        .join("&");
    }

    const formData = {
      "form-name": "quiz-submission",
      ...answers,
      cuts: answers.cut.join(", "),
      includeSetting: answers.includeSetting ? "Yes" : "No"
    };

    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: encode(formData)
    })
    .then(() => {
      console.log("Form submitted successfully!");
    })
    .catch(error => {
      console.error("Form submission error:", error);
    });
  };

  const nextStep = async () => {
    const nextStepNumber = step + 1;
    setStep(nextStepNumber);
    navigate(`/quiz/${nextStepNumber}`);
    
    // Subscribe to mailing list after contact info (now step 1)
    if (step === 1) {
      try {
        const response = await fetch('/.netlify/functions/sender-subscribe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: answers.email,
            firstName: '',
            lastName: ''
          })
        });
        const result = await response.json();
        if (!response.ok) {
          console.error('Subscription error:', result.error);
        }
      } catch (error) {
        console.error('Subscription error:', error);
      }
    }
    
    // If this is the last step, submit the form
    if (nextStepNumber === 9) {
      handleFormSubmit();
    }
  };

  const prevStep = () => {
    const prevStepNumber = step - 1;
    setStep(prevStepNumber);
    navigate(`/quiz/${prevStepNumber}`);
  };

  const renderStepIndicator = () => {
    return (
      <div className="step-indicator">
        {[1,2,3,4,5,6,7,8,9].map((stepNumber) => (
          <div 
            key={stepNumber}
            className={`step-dot ${stepNumber === step ? 'active' : ''} ${stepNumber < step ? 'completed' : ''}`}
          >
            <span className="step-number">{stepNumber}</span>
            {stepNumber < 9 && <div className="step-line" />}
          </div>
        ))}
      </div>
    );
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        // Contact Info (moved from step 8)
        return (
          <div className="quiz-step">
            <div className="step-header">
              <span className="step-label">Step 1 of 9</span>
              <h2>Let's craft your perfect ring</h2>
            </div>
            <div className="step-content">
              <div className="contact-fields">
                <div className="contact-field">
                  <label htmlFor="email">Email address</label>
                  <input
                    id="email"
                    type="email"
                    value={answers.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="Enter your email"
                    className="quiz-input"
                  />
                </div>
              </div>
              <div className="navigation-buttons">
                <a href="https://elysianstonedesign.com" className="quiz-button secondary" target="_blank" rel="noopener noreferrer">Back</a>
                <button 
                  className="quiz-button"
                  onClick={nextStep}
                  disabled={!answers.email}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        );

      case 2:
        // Budget (was step 1)
        const budgetRanges = [
          {
            value: 'under 2000',
            label: 'Under $2,000',
            description: 'Perfect for a budget-friendly minimal design'
          },
          {
            value: '2000-6000',
            label: '$2,000 - $6,000',
            description: 'Ideal for quality craftsmanship and design'
          },
          {
            value: '6000-10000',
            label: '$6,000 - $10,000',
            description: 'Premium selection with exceptional quality'
          },
          {
            value: '10000-plus',
            label: '$10,000+',
            description: 'Luxury pieces with the finest materials'
          },
          {
            value: 'flexible',
            label: 'I\'m flexible',
            description: 'Show me options across all price ranges'
          }
        ];

        return (
          <div className="quiz-step">
            <div className="step-header">
              <span className="step-label">Step 2 of 9</span>
              <h2>What is your budget?</h2>
              <p className="step-description">Select your price range to help us find the perfect diamond within your budget.</p>
            </div>
            <div className="options-grid">
              {budgetRanges.map(range => (
                <button
                  key={range.value}
                  className={`option-button ${answers.budget === range.value ? 'selected' : ''}`}
                  onClick={() => handleInputChange('budget', range.value)}
                  data-value={range.value}
                >
                  <div className="option-content">
                    <h3>{range.label}</h3>
                    <p>{range.description}</p>
                  </div>
                </button>
              ))}
            </div>
            <div className="navigation-buttons">
              <button className="quiz-button secondary" onClick={prevStep}>Back</button>
              <button 
                className="quiz-button"
                onClick={nextStep}
                disabled={!answers.budget}
              >
                Next
              </button>
            </div>
          </div>
        );

      case 3:
        const cuts = [
          {
            name: 'Round Brilliant',
            diagram: '/images/shape-round.png',
            photo: '/images/shape-round.png',
            description: 'The most popular cut with maximum sparkle and timeless appeal.',
            pros: ['Maximum brilliance', 'Classic choice', 'Best light performance']
          },
          {
            name: 'Princess',
            diagram: '/images/shape-princess.png',
            photo: '/images/shape-princess.png',
            description: 'Modern square cut that makes the diamond appear larger.',
            pros: ['Contemporary look', 'Excellent sparkle', 'Larger appearance']
          },
          {
            name: 'Radiant',
            diagram: '/images/shape-radiant.png',
            photo: '/images/shape-radiant.png',
            description: 'A brilliant-cut square or rectangular diamond with cropped corners and exceptional sparkle.',
            pros: ['Maximum brilliance', 'Modern square shape', 'Excellent fire']
          },
          {
            name: 'Cushion',
            diagram: '/images/shape-cushion.png',
            photo: '/images/shape-cushion.png',
            description: 'Soft corners with romantic appeal and exceptional fire.',
            pros: ['Vintage style', 'Romantic look', 'Great fire']
          },
          {
            name: 'Oval',
            diagram: '/images/shape-oval.png',
            photo: '/images/shape-oval.png',
            description: 'Elongated shape that creates the illusion of length.',
            pros: ['Elongates finger', 'Excellent sparkle', 'Modern classic']
          },
          {
            name: 'Emerald',
            diagram: '/images/shape-emerald.png',
            photo: '/images/shape-emerald.png',
            description: 'Sophisticated step cut with elegant hall-of-mirrors effect.',
            pros: ['Elegant look', 'Excellent clarity', 'Unique sparkle']
          },
          {
            name: 'Asscher',
            diagram: '/images/shape-asscher.png',
            photo: '/images/shape-asscher.png',
            description: 'Square step cut with cropped corners and exceptional brilliance.',
            pros: ['Art deco style', 'Excellent clarity', 'Unique sparkle']
          },
          {
            name: 'Pear',
            diagram: '/images/shape-pear.png',
            photo: '/images/shape-pear.png',
            description: 'Unique teardrop shape combining round brilliance with marquise elegance.',
            pros: ['Distinctive look', 'Elongates finger', 'Excellent sparkle']
          },
          {
            name: 'Marquise',
            diagram: '/images/shape-marquise.png',
            photo: '/images/shape-marquise.png',
            description: 'Elongated cut with pointed ends for maximum size appearance.',
            pros: ['Larger appearance', 'Elegant look', 'Great sparkle']
          },
          {
            name: 'Heart',
            diagram: '/images/shape-heart.png',
            photo: '/images/shape-heart.png',
            description: 'Romantic heart shape that symbolizes love and affection.',
            pros: ['Romantic look', 'Unique shape', 'Symbolic meaning']
          },
          {
            name: 'Unsure',
            description: 'Create your own unique diamond design with our expert guidance.',
            pros: ['Unique design', 'Personal touch', 'Expert consultation'],
            photo: '/images/unknown.png',
            diagram: '/images/unknown.png',

          }
        ];
        return (
          <div className="quiz-step quiz-step-cuts">
            <div className="step-header">
              <span className="step-label">Step 3 of 9</span>
              <h2>Which shape catches your eye?</h2>
              <p className="step-description">The shape determines how well a diamond reflects light. Each has its own unique characteristics.</p>
            </div>
            <div className="step-content">
              <div className="cuts-grid">
                {cuts.map(cut => (
                  <div 
                    key={cut.name}
                    className={`cut-card ${answers.cut.includes(cut.name) ? 'selected' : ''} ${cut.name === 'Custom or Open to Suggestions' ? 'custom-option' : ''}`}
                  >
                    <div className="cut-header">
                      <h3>{cut.name}</h3>
                      <button 
                        className="cut-select-button"
                        onClick={() => handleInputChange('cut', cut.name)}
                      >
                        {answers.cut.includes(cut.name) ? '✓' : '+'}
                      </button>
                    </div>
                      <div 
                        className="cut-image-container"
                        onClick={() => toggleCutView(cut.name)}
                      >
                        <div className="cut-image-placeholder">
                          <img 
                            src={cutViewMode[cut.name] === 'photo' ? cut.photo : cut.diagram} 
                            alt={`${cut.name} cut`}
                          />
                          <div className="view-toggle">
                            {cutViewMode[cut.name] === 'photo' ? 'View Diagram' : 'View on Hand'}
                          </div>
                        </div>
                      </div>
                    <div className="cut-info">
                      <p className="cut-description">{cut.description}</p>
                      <ul className="cut-pros">
                        {cut.pros.map((pro, index) => (
                          <li key={index}>{pro}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
              <div className="navigation-buttons">
                <button className="quiz-button secondary" onClick={prevStep}>Back</button>
                <button 
                  className="quiz-button"
                  onClick={nextStep}
                  disabled={answers.cut.length === 0}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        );

      case 4:
        const caratRanges = [
          {
            range: '0.5 - 1.0',
            price: '$500 - $1,300',
            description: 'Perfect for everyday wear and subtle elegance',
            image: '/images/carat-0-1.png',
            info: 'Most popular range for engagement rings. Excellent balance of size and value.'
          },
          {
            range: '1.0 - 3.0',
            price: '$1,500 - $7,000',
            description: 'Striking presence without overwhelming',
            image: '/images/carat-1-3.png',
            info: 'Ideal for those seeking a noticeable diamond. Great value for size.'
          },
          {
            range: '3.0+',
            price: '$2,300 - $65,000+ (outliers)',
            description: 'Luxury and extraordinary presence',
            image: '/images/carat-3+.png',
            info: 'The pinnacle of diamond size. With the affordability of lab-grown diamonds, you can get a larger diamond than you ever thought possible.'
          },
          {
            range: 'I\'m flexible',
            price: 'Flexible',
            description: 'Open to suggestions',
            info: ''
          }
        ];
        return (
          <div className="quiz-step">
            <div className="step-header">
              <span className="step-label">Step 4 of 9</span>
              <h2>How big are we talking?</h2>
              <p className="step-description">Select your preferred carat range. All prices shown are for lab-grown diamonds of excellent cut and clarity.</p>
            </div>
            <div className="step-content">
              <div className="carat-grid">
                {caratRanges.map(range => (
                  <div 
                    key={range.range}
                    className={`carat-card ${answers.carat === range.range ? 'selected' : ''} ${range.range === 'I\'m flexible' ? 'flexible-option' : ''}`}
                    onClick={() => handleInputChange('carat', range.range)}
                  >
                    <div className="carat-container">
                      {range.image && (
                        <div 
                          className="carat-image"
                          style={{ 
                            backgroundImage: `url(${range.image})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center'
                          }}
                        />
                      )}
                      <div className="carat-info">
                        <h3>{range.range}</h3>
                        <p className="carat-description">{range.description}</p>
                        {range.info && <p className="carat-tip">{range.info}</p>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="navigation-buttons">
                <button className="quiz-button secondary" onClick={prevStep}>Back</button>
                <button 
                  className="quiz-button"
                  onClick={nextStep}
                  disabled={!answers.carat}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        );

      case 5:
        const clarityOptions = [
          {
            val: 'fl-if',
            grade: 'FL-IF',
            description: 'Perfect or near-perfect clarity',
            info: 'FL has no internal or external flaws, while IF has no internal flaws but may have minor surface blemishes that can be polished away.\n\n',
            tip: 'The most prestigious clarity grades, perfect for investment pieces.',
            image: '/images/clarity-fl-if.jpg'
          },
          {
            val: 'vvs',
            grade: 'VVS1-VVS2',
            description: 'Minute inclusions, difficult to see under magnification',
            info: 'Tiny inclusions that are very difficult to see under 10x magnification. Completely eye-clean.\n\n\n',
            tip: 'Great balance of quality and value. Perfect for engagement rings.',
            image: '/images/clarity-vvs1-vvs2.jpg'
          },
          {
            val: 'vs',
            grade: 'VS1-VS2',
            description: 'Minor inclusions, visible under magnification',
            info: 'Small inclusions that are visible under 10x magnification but typically not to the naked eye. Excellent value.\n\n',
            tip: 'Most popular choice. Offers the best balance of quality and price.',
            badge: 'Most Popular',
            image: '/images/clarity-vs1-vs2.jpg'
          },
          {
            val: 'si',
            grade: 'SI1-SI2',
            description: 'Noticeable inclusions, may be visible to naked eye',
            info: 'Inclusions are noticeable under 10x magnification. SI1 is often eye-clean, while SI2 may have visible inclusions.\n\n',
            tip: 'SI1 is often eye-clean and offers significant savings.',
            badge: 'Best Value',
            image: '/images/clarity-si1-si2.jpg'
          },
          {
            val: 'i',
            grade: 'I don\'t know',
            description: 'Open to suggestions',
            info: '',
            tip: '',
            image: null
          }
        ];
        return (
          <div className="quiz-step">
            <div className="step-header">
              <span className="step-label">Step 5 of 9</span>
              <h2>What clarity level are you looking for?</h2>
              <p className="step-description">Clarity refers to the absence of internal and external flaws. Higher clarity means fewer imperfections, but also higher price.</p>
            </div>
            <div className="step-content">
              <div className="clarity-grid">
                {clarityOptions.map(clarity => (
                  <div 
                    key={clarity.grade}
                    className={`clarity-card ${answers.clarity === clarity.grade ? 'selected' : ''} ${clarity.badge ? clarity.badge.toLowerCase().replace(' ', '-') : ''} ${clarity.val}`}
                    onClick={() => handleInputChange('clarity', clarity.grade)}
                    data-grade={clarity.grade.replaceAll('\'', '').replaceAll('-', '')}
                  >
                    {clarity.grade === 'VS1-VS2' && (
                      <div className="clarity-ribbon">Most Popular</div>
                    )}
                    {clarity.image && (
                      <div 
                        className="clarity-image"
                        style={{ backgroundImage: `url(${clarity.image})` }}
                      />
                    )}
                    <div className="clarity-header">
                      <h3>{clarity.grade}</h3>
                      {clarity.badge && <span className={`clarity-badge ${clarity.badge.toLowerCase().replace(' ', '-')}`} data-text={clarity.badge}></span>}
                      <p className="clarity-description">{clarity.description}</p>
                    </div>
                    <div className="clarity-info">
                      <p className="clarity-detail">{clarity.info}</p>
                      <p className="clarity-tip">{clarity.tip}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="navigation-buttons">
                <button className="quiz-button secondary" onClick={prevStep}>Back</button>
                <button 
                  className="quiz-button"
                  onClick={nextStep}
                  disabled={!answers.clarity}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        );

      case 6:
        const colorOptions = [
          {
            value: 'colorless',
            label: 'Colorless (D-F)',
            description: 'Premium white diamonds with exceptional brilliance',
            badge: 'Premium',
            image: '/images/color-d-f.jpg'
          },
          {
            value: 'near-colorless',
            label: 'Near Colorless (G-H)',
            description: 'Excellent value with minimal color visible',
            badge: 'Best Value',
            image: '/images/color-g-h.jpg'
          },
          {
            value: 'slightly-warm',
            label: 'Slightly Warm (I-J)',
            description: 'Subtle warmth with significant savings',
            image: '/images/color-i-j.jpg'
          },
          {
            value: 'I\'m open to suggestions/I want customization',
            label: 'I\'m open to suggestions/I want customization',
            description: 'Show me options across all color grades',
            image: null
          }
        ];
        return (
          <div className="quiz-step">
            <div className="step-header">
              <span className="step-label">Step 6 of 9</span>
              <h2>What color grade are you looking for?</h2>
              <p className="step-description">Diamond color ranges from colorless to slightly warm. Higher grades mean less color but higher price.</p>
            </div>
            <div className="options-grid">
              {colorOptions.map(option => (
                <button
                  key={option.value}
                  className={`option-button ${answers.color === option.value ? 'selected' : ''} ${option.badge ? option.badge.toLowerCase().replace(' ', '-') : ''}`}
                  onClick={() => handleInputChange('color', option.value)}
                  data-value={option.value}
                >
                  <div className="option-content">
                    <h3>{option.label}</h3>
                    <p>{option.description}</p>
                    {option.badge && (
                      <span className="option-badge ribbon">{option.badge}</span>
                    )}
                  </div>
                  {option.image && (
                    <div 
                      className="color-image"
                      style={{ backgroundImage: `url(${option.image})` }}
                    />
                  )}
                </button>
              ))}
            </div>
            <div className="navigation-buttons">
              <button className="quiz-button secondary" onClick={prevStep}>Back</button>
              <button 
                className="quiz-button"
                onClick={nextStep}
                disabled={!answers.color}
              >
                Next
              </button>
            </div>
          </div>
        );

      case 7:
        const cutQualityOptions = [
          {
            value: 'excellent',
            label: 'Only the best',
            description: 'Ideal/Excellent cut for maximum sparkle and brilliance',
            badge: 'Recommended'
          },
          {
            value: 'very-good',
            label: 'Very good is fine with me',
            description: 'Excellent sparkle with better value',
            badge: ''
          },
          {
            value: 'flexible',
            label: 'I\'m open to suggestions',
            description: 'Show me options across all cut qualities'
          }
        ];
        return (
          <div className="quiz-step">
            <div className="step-header">
              <span className="step-label">Step 7 of 9</span>
              <h2>How important is sparkle and brilliance to you?</h2>
              <p className="step-description">The cut quality determines how well a diamond reflects light and creates that signature sparkle.</p>
            </div>
            <div className="options-grid quality">
              {cutQualityOptions.map(option => (
                <button
                  key={option.value}
                  className={`option-button ${answers.cutQuality === option.value ? 'selected' : ''} ${option.badge ? option.badge.toLowerCase().replace(' ', '-') : ''}`}
                  onClick={() => handleInputChange('cutQuality', option.value)}
                  data-value={option.value}
                >
                  <div className="option-content">
                    <h3>{option.label}</h3>
                    <p>{option.description}</p>
                    {option.badge && (
                      <span className="option-badge ribbon">{option.badge}</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
            <div className="navigation-buttons">
              <button className="quiz-button secondary" onClick={prevStep}>Back</button>
              <button 
                className="quiz-button"
                onClick={nextStep}
                disabled={!answers.cutQuality}
              >
                Next
              </button>
            </div>
          </div>
        );

      case 8:
        const settingOptions = [
          {
            type: 'Solitaire',
            description: 'Classic & Timeless',
            image: '/images/setting-solitaire.png'
          },
          {
            type: 'Halo',
            description: 'Romantic & Elegant',
            image: '/images/setting-halo.png'
          },
          {
            type: 'Three-Stone',
            description: 'Symbolic',
            image: '/images/setting-three-stone.png'
          },
          {
            type: 'Pave',
            description: 'Delicate detail',
            image: '/images/setting-pave.png'
          },
          {
            type: 'Bezel',
            description: 'Modern & Minimalist',
            image: '/images/setting-bezel.png'
          },
          {
            type: "I'm open to suggestions or customizations",
            description: 'Show me all options',
            image: null
          }
        ];
        return (
          <div className="quiz-step">
            <div className="step-header">
              <span className="step-label">Step 8 of 9</span>
              <h2>What type of setting do you prefer?</h2>
            </div>
            <div className="step-content">
              <div className="include-setting-checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={answers.includeSetting}
                    onChange={(e) => handleInputChange('includeSetting', e.target.checked)}
                  />
                  Include setting
                </label>
              </div>
              <div className="options-grid settings-grid">
                {settingOptions.map(setting => (
                  <div
                    key={setting.type}
                    className={`setting-option ${answers.setting === setting.type ? 'selected' : ''} ${setting.type === "I'm open to suggestions or customizations" ? 'suggestions-option' : ''}`}
                    onClick={() => handleInputChange('setting', setting.type)}
                  >
                    {setting.image && (
                      <div 
                        className={`setting-image ${setting.type === 'Solitaire' || setting.type === 'Halo' || setting.type === 'Three-Stone' ? 'setting-image-zoom-75' : ''}`}
                        style={{ backgroundImage: `url(${setting.image})` }}
                      />
                    )}
                    <div className="setting-info">
                      <h3>{setting.type}</h3>
                      <p>{setting.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="navigation-buttons">
                <button className="quiz-button secondary" onClick={prevStep}>Back</button>
                <button 
                  className="quiz-button"
                  onClick={nextStep}
                  disabled={!answers.setting}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        );

      case 9:
        // Summary (was step 9)
        // Log all quiz answers
        console.log('Quiz Results:', {
          'Contact Information': {
            'Email': answers.email,
            'Phone': answers.phone || 'Not provided'
          },
          'Budget': answers.budget,
          'Diamond Preferences': {
            'Selected Cuts': answers.cut,
            'Carat Range': answers.carat,
            'Clarity Grade': answers.clarity,
            'Color Grade': answers.color,
            'Cut Quality': answers.cutQuality
          },
          'Setting Preferences': {
            'Include Setting': answers.includeSetting ? 'Yes' : 'No',
            'Setting Type': answers.setting || 'Not selected'
          }
        });

        return (
          <div className="quiz-step summary">
            <div className="step-header">
              <span className="step-label">Step 9 of 9</span>
              <h2>We'll be in touch within 24 hours!</h2>
              <p className="step-description">We're crafting your personalized diamond experience.</p>
            </div>
            <div className="step-content">
              <div className="summary-message">
                <h3>Thank You for Sharing Your Vision!</h3>
                <p>Our diamond experts are carefully analyzing your preferences to find the perfect match for your unique style and desires.</p>
              </div>

              <div className="matching-process">
                <p>We'll be in touch within 24 hours!</p>
              </div>

              <div className="next-steps">
                <h4>What's Next?</h4>
                <ul>
                  <li>Our experts will review your preferences</li>
                  <li>We'll curate a selection of perfect matches</li>
                  <li>You'll receive a personalized email with your results</li>
                </ul>
              </div>
              
              <div className="back-to-home">
                <a href="https://elysianstonedesign.com" className="quiz-button secondary" target="_blank" rel="noopener noreferrer">
                  Back to Home
                </a>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="quiz-container">
      {/* Hidden Netlify Form */}
      <form
        name="quiz-submission"
        method="POST"
        data-netlify="true"
        data-netlify-honeypot="bot-field"
        hidden
      >
        <input type="hidden" name="form-name" value="quiz-submission" />
        <input type="text" name="email" />
        <input type="text" name="phone" />
        <input type="text" name="budget" />
        <input type="text" name="cuts" />
        <input type="text" name="carat" />
        <input type="text" name="clarity" />
        <input type="text" name="color" />
        <input type="text" name="cutQuality" />
        <input type="text" name="includeSetting" />
        <input type="text" name="setting" />
      </form>

      {renderStepIndicator()}
      {renderStep()}
    </div>
  );
};

export default Quiz; 